import React from "react";
import { motion } from "framer-motion";

interface Step {
  icon: string;
  title: string;
  desc: string;
}

interface StepsProps {
  steps: Step[];
}

const iconMap: Record<string, React.ReactNode> = {
  wallet: (
    <svg width="48" height="48" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="7" width="18" height="10" rx="3" fill="#8b5cf6" fillOpacity="0.2"/>
      <rect x="3" y="7" width="18" height="10" rx="3" stroke="#8b5cf6" strokeWidth="2"/>
      <circle cx="17" cy="12" r="2" fill="#8b5cf6"/>
    </svg>
  ),
  card: (
    <svg width="48" height="48" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="7" width="18" height="10" rx="2" fill="#3b82f6" fillOpacity="0.2"/>
      <rect x="3" y="7" width="18" height="10" rx="2" stroke="#3b82f6" strokeWidth="2"/>
      <rect x="6" y="14" width="8" height="2" rx="1" fill="#3b82f6"/>
    </svg>
  ),
  rocket: (
    <svg width="48" height="48" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2c2.5 2.5 4 6.5 4 10s-1.5 7.5-4 10c-2.5-2.5-4-6.5-4-10s1.5-7.5 4-10z" fill="#f59e0b" fillOpacity="0.2" stroke="#f59e0b" strokeWidth="2"/>
      <circle cx="12" cy="12" r="3" fill="#f59e0b"/>
    </svg>
  )
};

const Steps: React.FC<StepsProps> = ({ steps }) => (
  <section data-section="steps" className="py-8 md:py-12 bg-transparent relative">
    <div className="max-w-5xl mx-auto px-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.2, duration: 0.6 }}
              className="relative flex flex-col items-center bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-6 shadow-xl min-h-[180px] border border-white/10 group hover:border-purple-400/50 transition-all"
            >
              {/* Step Number */}
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {i + 1}
              </div>
              
              <motion.div
                className="mb-4"
                whileHover={{ scale: 1.1, rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
                aria-hidden="true"
              >
                {iconMap[step.icon]}
              </motion.div>
              
              <h3 className="font-semibold text-lg mb-2 text-white text-center group-hover:text-purple-300 transition-colors" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                {step.title}
              </h3>
              
              <p className="text-sm text-white/70 text-center leading-relaxed">
                {step.desc}
              </p>
            </motion.div>
            
            {/* Arrow Between Steps (hidden on mobile, shown on desktop) */}
            {i < steps.length - 1 && (
              <motion.div
                className="hidden sm:flex items-center justify-center -mx-4 text-purple-400/50"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.2 + 0.3, duration: 0.6 }}
              >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.div>
            )}
          </React.Fragment>
        ))}
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="text-center text-sm text-cyan-300/80 mt-6 font-medium"
      >
        ⏱️ Average experience time 8 to 12 minutes
      </motion.div>
    </div>
  </section>
);

export default Steps;
